import { create } from "zustand";
import { supabase } from "@/lib/supabase/client";
import type { User, Booking, Experience } from "@/lib/types/supabase";
import { useCallback } from "react";
import { useAuth } from '@/lib/auth/store';

/**
 * Enhanced admin service with error handling and type safety
 * Better structured for future agent interactions
 */
const adminService = {
  // User operations
  async getUsers() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*, creator_profiles(*)')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Add compatibility mapping for creator_profiles -> creator_profile
      const processedData = (data || []).map(user => {
        if (user && Array.isArray(user.creator_profiles) && user.creator_profiles.length > 0) {
          return {
            ...user,
            creator_profile: user.creator_profiles[0] || null
          };
        }
        return user;
      });
      
      return processedData;
    } catch (error) {
      console.error("[AdminService] Error fetching users:", error);
      throw error;
    }
  },
  
  // We don't use is_approved since it doesn't exist in schema
  async getPendingUsers() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*, creator_profiles(*)')
        .eq('role', 'user')
        .order('created_at', { ascending: false })
        .limit(25);
      
      if (error) throw error;
      
      // Add compatibility mapping for creator_profiles -> creator_profile
      const processedData = (data || []).map(user => {
        if (user && Array.isArray(user.creator_profiles) && user.creator_profiles.length > 0) {
          return {
            ...user,
            creator_profile: user.creator_profiles[0] || null
          };
        }
        return user;
      });
      
      return processedData;
    } catch (error) {
      console.error("[AdminService] Error fetching pending users:", error);
      throw error;
    }
  },
  
  async getPendingCreators() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*, creator_profiles(*)')
        .eq('role', 'creator')
        .order('created_at', { ascending: false })
        .limit(25);
      
      if (error) throw error;
      
      // Add compatibility mapping for creator_profiles -> creator_profile
      const processedData = (data || []).map(user => {
        if (user && Array.isArray(user.creator_profiles) && user.creator_profiles.length > 0) {
          return {
            ...user,
            creator_profile: user.creator_profiles[0] || null
          };
        }
        return user;
      });
      
      return processedData;
    } catch (error) {
      console.error("[AdminService] Error fetching pending creators:", error);
      throw error;
    }
  },
  
  async approveUser(userId: string) {
    try {
      // In our schema we don't have is_approved, so this is a placeholder
      // Will need to update based on actual schema
      const { error } = await supabase
        .from('users')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', userId);
      
      if (error) throw error;
    } catch (error) {
      console.error("[AdminService] Error approving user:", error);
      throw error;
    }
  },
  
  async rejectUser(userId: string) {
    try {
      // In our schema we don't have is_approved, so this is a placeholder
      // Will need to update based on actual schema
      const { error } = await supabase
        .from('users')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', userId);
      
      if (error) throw error;
    } catch (error) {
      console.error("[AdminService] Error rejecting user:", error);
      throw error;
    }
  },
  
  async deleteUser(userId: string) {
    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);
      
      if (error) throw error;
    } catch (error) {
      console.error("[AdminService] Error deleting user:", error);
      throw error;
    }
  },
  
  async updateUserRole(userId: string, newRole: 'user' | 'creator' | 'admin') {
    try {
      const { error } = await supabase
        .from('users')
        .update({ role: newRole })
        .eq('id', userId);
      
      if (error) throw error;
    } catch (error) {
      console.error("[AdminService] Error updating user role:", error);
      throw error;
    }
  },
  
  // Experience operations
  async getExperiences() {
    try {
      // Fix the creator_profiles relation - we need to use users table joined with creator_profiles
      const { data, error } = await supabase
        .from('experiences')
        .select(`
          *,
          creator:creator_id (
            id, 
            full_name
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return (data || []).map((exp) => ({
        ...exp,
        creator_name: exp.creator?.full_name || 'Unknown Creator'
      }));
    } catch (error) {
      console.error("[AdminService] Error fetching experiences:", error);
      throw error;
    }
  },
  
  async getPendingExperiences() {
    try {
      const { data, error } = await supabase
        .from('experiences')
        .select(`
          *,
          creator:creator_id (
            id, 
            full_name
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return (data || []).map((exp) => ({
        ...exp,
        creator_name: exp.creator?.full_name || 'Unknown Creator'
      }));
    } catch (error) {
      console.error("[AdminService] Error fetching pending experiences:", error);
      throw error;
    }
  },
  
  async approveExperience(experienceId: string) {
    try {
      const { error } = await supabase
        .from('experiences')
        .update({ status: 'approved' })
        .eq('id', experienceId);
      
      if (error) throw error;
    } catch (error) {
      console.error("[AdminService] Error approving experience:", error);
      throw error;
    }
  },
  
  async rejectExperience(experienceId: string) {
    try {
      const { error } = await supabase
        .from('experiences')
        .update({ status: 'rejected' })
        .eq('id', experienceId);
      
      if (error) throw error;
    } catch (error) {
      console.error("[AdminService] Error rejecting experience:", error);
      throw error;
    }
  },
  
  async deleteExperience(experienceId: string) {
    try {
      const { error } = await supabase
        .from('experiences')
        .delete()
        .eq('id', experienceId);
      
      if (error) throw error;
    } catch (error) {
      console.error("[AdminService] Error deleting experience:", error);
      throw error;
    }
  },
  
  // Booking operations
  async getBookings() {
    try {
      // Fix the ambiguous relationship by specifying the exact relation keys
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          booking_user:user_id (
            id, 
            full_name,
            email
          ),
          booking_experience:experience_id (
            id,
            title,
            price
          )
        `)
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      
      return (data || []).map(booking => ({
        ...booking,
        experience_name: booking.booking_experience?.title || 'Unknown Experience',
        amount: booking.total_amount || 0
      }));
    } catch (error) {
      console.error("[AdminService] Error fetching bookings:", error);
      throw error;
    }
  },
  
  async getStats() {
    try {
      // Get user count
      const { count: userCount, error: userError } = await supabase
        .from('users')
        .select('id', { count: 'exact', head: true });
      
      if (userError) throw userError;
      
      // Get booking count
      const { count: bookingCount, error: bookingError } = await supabase
        .from('bookings')
        .select('id', { count: 'exact', head: true });
      
      if (bookingError) throw bookingError;
      
      // Get experience count
      const { count: experienceCount, error: experienceError } = await supabase
        .from('experiences')
        .select('id', { count: 'exact', head: true });
      
      if (experienceError) throw experienceError;
      
      // Get total revenue
      const { data: bookings, error: revenueError } = await supabase
        .from('bookings')
        .select('total_amount')
        .eq('payment_status', 'paid');
      
      if (revenueError) throw revenueError;
      
      const revenue = (bookings || []).reduce((sum, booking) => 
        sum + (booking.total_amount || 0), 0);
      
      return {
        totalUsers: userCount || 0,
        totalBookings: bookingCount || 0,
        totalExperiences: experienceCount || 0,
        revenue: revenue || 0
      };
    } catch (error) {
      console.error("[AdminService] Error fetching statistics:", error);
      throw error;
    }
  },
  
  async getAdminActions() {
    try {
      const { data, error } = await supabase
        .from('admin_actions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("[AdminService] Error fetching admin actions:", error);
      throw error;
    }
  },
  
  async logAction(action: any) {
    try {
      const { data, error } = await supabase
        .from('admin_actions')
        .insert({
          ...action,
          created_at: new Date().toISOString(),
        })
        .select();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error("[AdminService] Error logging admin action:", error);
      throw error;
    }
  }
};

interface AdminAction {
  id: string;
  type: 'user_approval' | 'experience_approval' | 'booking_update' | 'stats_update';
  status: 'pending' | 'completed' | 'failed';
  data: any;
  created_at: string;
  completed_at?: string;
  error?: string;
}

interface AdminState {
  // Core state
  isInitialized: boolean;
  error: string | null;
  
  // Data
  users: User[];
  pendingUsers: User[];
  pendingCreators: User[];
  bookings: Booking[];
  experiences: Experience[];
  pendingExperiences: Experience[];
  actions: AdminAction[];
  
  // Stats
  stats: {
    totalUsers: number;
    totalBookings: number;
    totalExperiences: number;
    revenue: number;
  };
  
  // Core functions
  initialize: () => Promise<(() => void) | undefined>;
  
  // User operations
  fetchUsers: () => Promise<void>;
  fetchPendingUsers: () => Promise<void>;
  fetchPendingCreators: () => Promise<void>;
  approveUser: (userId: string) => Promise<void>;
  rejectUser: (userId: string) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
  updateUserRole: (userId: string, newRole: 'user' | 'creator' | 'admin') => Promise<void>;
  
  // Experience operations
  fetchExperiences: () => Promise<void>;
  fetchPendingExperiences: () => Promise<void>;
  approveExperience: (experienceId: string) => Promise<void>;
  rejectExperience: (experienceId: string) => Promise<void>;
  deleteExperience: (experienceId: string) => Promise<void>;
  
  // Booking operations
  fetchBookings: () => Promise<void>;
  updateBookingStatus: (bookingId: string, status: Booking['status']) => Promise<void>;
  
  // Stats and tracking
  fetchStats: () => Promise<void>;
  fetchAdminActions: () => Promise<void>;
  logAction: (action: Omit<AdminAction, 'id' | 'created_at'>) => Promise<void>;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  // Initial state
  isInitialized: false,
  error: null,
  
  // Data
  users: [],
  pendingUsers: [],
  pendingCreators: [],
  bookings: [],
  experiences: [],
  pendingExperiences: [],
  actions: [],
  
  // Stats
  stats: {
    totalUsers: 0,
    totalBookings: 0,
    totalExperiences: 0,
    revenue: 0,
  },

  // User operations
  fetchUsers: async () => {
    try {
      const users = await adminService.getUsers();
      set({ users: users as User[] });
    } catch (error: any) {
      console.error("[AdminStore] Error fetching users:", error);
      set({ error: error.message });
    }
  },

  fetchPendingUsers: async () => {
    try {
      const users = await adminService.getPendingUsers();
      set({ pendingUsers: users as User[] });
    } catch (error: any) {
      console.error("[AdminStore] Error fetching pending users:", error);
      // Don't set global error - just log it
    }
  },

  fetchPendingCreators: async () => {
    try {
      const users = await adminService.getPendingCreators();
      set({ pendingCreators: users as User[] });
    } catch (error: any) {
      console.error("[AdminStore] Error fetching pending creators:", error);
      // Don't set global error - just log it
    }
  },

  fetchBookings: async () => {
    try {
      const bookings = await adminService.getBookings();
      set({ bookings: bookings as Booking[] });
    } catch (error: any) {
      console.error("[AdminStore] Error fetching bookings:", error);
      // Don't set global error - just log it
    }
  },

  fetchExperiences: async () => {
    try {
      const experiences = await adminService.getExperiences();
      set({ experiences: experiences as Experience[] });
    } catch (error: any) {
      console.error("[AdminStore] Error fetching experiences:", error);
      // Don't set global error - just log it
    }
  },

  fetchPendingExperiences: async () => {
    try {
      const experiences = await adminService.getPendingExperiences();
      set({ pendingExperiences: experiences as Experience[] });
    } catch (error: any) {
      console.error("[AdminStore] Error fetching pending experiences:", error);
      // Don't set global error - just log it
    }
  },

  fetchStats: async () => {
    try {
      const stats = await adminService.getStats();
      set({ stats });
    } catch (error: any) {
      console.error("[AdminStore] Error fetching stats:", error);
      // Don't set global error - just log it
    }
  },

  fetchAdminActions: async () => {
    try {
      const actions = await adminService.getAdminActions();
      set({ actions: actions as AdminAction[] });
    } catch (error: any) {
      console.error("[AdminStore] Error fetching admin actions:", error);
      // Don't set global error - just log it
    }
  },

  // Core initialization function - now more resilient to partial failures
  initialize: async () => {
    const { user } = useAuth.getState();
    console.log("[AdminStore] Initialize called with user:", {
      id: user?.id,
      role: user?.role,
      email: user?.email
    });
    
    // Check for user and role
    if (!user) {
      console.error("[AdminStore] No user found in auth store");
      set({ error: "No user found" });
      return;
    }

    if (user.role !== 'admin') {
      console.error("[AdminStore] User is not an admin. Role:", user.role);
      set({ error: "Unauthorized: User is not an admin" });
      return;
    }

    let subscriptions: (() => void)[] = [];

    try {
      // Set loading state
      set({ isInitialized: false, error: null });
      console.log("[AdminStore] Starting initialization for admin:", user.id);

      // Fetch core data first - these are the most important
      await get().fetchUsers();
      await get().fetchExperiences();
      
      // Mark initialized so the UI becomes usable
      set({ isInitialized: true });
      
      // Then fetch the rest in parallel without blocking
      Promise.allSettled([
        get().fetchPendingUsers(),
        get().fetchPendingCreators(),
        get().fetchBookings(),
        get().fetchPendingExperiences(),
        get().fetchStats(),
        get().fetchAdminActions(),
      ]).then(results => {
        // Log any individual fetch failures but don't block the UI
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error(`[AdminStore] Background fetch #${index} failed:`, result.reason);
          }
        });
      });

      // Set up real-time subscriptions
      const usersSubscription = supabase
        .channel('users_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'users' }, () => {
          get().fetchUsers();
          get().fetchPendingUsers();
          get().fetchPendingCreators();
          get().fetchStats();
        })
        .subscribe();

      const bookingsSubscription = supabase
        .channel('bookings_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, () => {
          get().fetchBookings();
          get().fetchStats();
        })
        .subscribe();

      const experiencesSubscription = supabase
        .channel('experiences_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'experiences' }, () => {
          get().fetchExperiences();
          get().fetchPendingExperiences();
          get().fetchStats();
        })
        .subscribe();

      const actionsSubscription = supabase
        .channel('actions_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'admin_actions' }, () => {
          get().fetchAdminActions();
        })
        .subscribe();

      subscriptions = [
        () => usersSubscription.unsubscribe(),
        () => bookingsSubscription.unsubscribe(),
        () => experiencesSubscription.unsubscribe(),
        () => actionsSubscription.unsubscribe(),
      ];

      console.log("[AdminStore] Successfully initialized for admin:", user.id);
    } catch (error: any) {
      console.error("[AdminStore] Error during initialization:", error);
      set({ error: error.message, isInitialized: true });
    }

    // Return cleanup function
    return () => {
      console.log("[AdminStore] Running cleanup for admin:", user.id);
      subscriptions.forEach(cleanup => cleanup());
    };
  },

  // User operations - simplified with service layer
  approveUser: async (userId: string) => {
    try {
      await adminService.approveUser(userId);
      await get().logAction({
        type: 'user_approval',
        status: 'completed',
        data: { userId },
        completed_at: new Date().toISOString(),
      });
      await get().fetchUsers();
    } catch (error: any) {
      console.error("[AdminStore] Error approving user:", error);
      await get().logAction({
        type: 'user_approval',
        status: 'failed',
        data: { userId },
        error: error.message,
        completed_at: new Date().toISOString(),
      });
    }
  },

  rejectUser: async (userId: string) => {
    try {
      await adminService.rejectUser(userId);
      await get().logAction({
        type: 'user_approval',
        status: 'failed',
        data: { userId },
        completed_at: new Date().toISOString(),
      });
      await get().fetchUsers();
    } catch (error: any) {
      console.error("[AdminStore] Error rejecting user:", error);
      await get().logAction({
        type: 'user_approval',
        status: 'failed',
        data: { userId, error: error.message },
        completed_at: new Date().toISOString(),
      });
    }
  },

  deleteUser: async (userId: string) => {
    try {
      await adminService.deleteUser(userId);
      await get().logAction({
        type: 'user_approval',
        status: 'completed',
        data: { userId, action: 'delete' },
        completed_at: new Date().toISOString(),
      });
      await get().fetchUsers();
    } catch (error: any) {
      console.error("[AdminStore] Error deleting user:", error);
      await get().logAction({
        type: 'user_approval',
        status: 'failed',
        data: { userId, action: 'delete' },
        error: error.message,
        completed_at: new Date().toISOString(),
      });
    }
  },

  updateUserRole: async (userId: string, newRole: 'user' | 'creator' | 'admin') => {
    try {
      await adminService.updateUserRole(userId, newRole);
      await get().logAction({
        type: 'user_approval',
        status: 'completed',
        data: { userId, newRole },
        completed_at: new Date().toISOString(),
      });
      await get().fetchUsers();
    } catch (error: any) {
      console.error("[AdminStore] Error updating user role:", error);
      await get().logAction({
        type: 'user_approval',
        status: 'failed',
        data: { userId, newRole },
        error: error.message,
        completed_at: new Date().toISOString(),
      });
    }
  },

  // Experience operations - simplified with service layer
  approveExperience: async (experienceId: string) => {
    try {
      await adminService.approveExperience(experienceId);
      await get().logAction({
        type: 'experience_approval',
        status: 'completed',
        data: { experienceId },
        completed_at: new Date().toISOString(),
      });
      await Promise.all([
        get().fetchExperiences(),
        get().fetchPendingExperiences(),
      ]);
    } catch (error: any) {
      console.error("[AdminStore] Error approving experience:", error);
      await get().logAction({
        type: 'experience_approval',
        status: 'failed',
        data: { experienceId },
        error: error.message,
        completed_at: new Date().toISOString(),
      });
    }
  },

  rejectExperience: async (experienceId: string) => {
    try {
      await adminService.rejectExperience(experienceId);
      await get().logAction({
        type: 'experience_approval',
        status: 'failed',
        data: { experienceId },
        completed_at: new Date().toISOString(),
      });
      await Promise.all([
        get().fetchExperiences(),
        get().fetchPendingExperiences(),
      ]);
    } catch (error: any) {
      console.error("[AdminStore] Error rejecting experience:", error);
      await get().logAction({
        type: 'experience_approval',
        status: 'failed',
        data: { experienceId },
        error: error.message,
        completed_at: new Date().toISOString(),
      });
    }
  },

  deleteExperience: async (experienceId: string) => {
    try {
      await adminService.deleteExperience(experienceId);
      await get().logAction({
        type: 'experience_approval',
        status: 'completed',
        data: { experienceId, action: 'delete' },
        completed_at: new Date().toISOString(),
      });
      await Promise.all([
        get().fetchExperiences(),
        get().fetchPendingExperiences(),
      ]);
    } catch (error: any) {
      console.error("[AdminStore] Error deleting experience:", error);
      await get().logAction({
        type: 'experience_approval',
        status: 'failed',
        data: { experienceId, action: 'delete' },
        error: error.message,
        completed_at: new Date().toISOString(),
      });
    }
  },

  updateBookingStatus: async (bookingId: string, status: Booking['status']) => {
    try {
      const { error } = await supabase
        .from("bookings")
        .update({ status })
        .eq("id", bookingId);

      if (error) throw error;

      await get().logAction({
        type: 'booking_update',
        status: 'completed',
        data: { bookingId, status },
        completed_at: new Date().toISOString(),
      });

      await get().fetchBookings();
    } catch (error: any) {
      console.error("[AdminStore] Error updating booking status:", error);
      await get().logAction({
        type: 'booking_update',
        status: 'failed',
        data: { bookingId, status },
        error: error.message,
        completed_at: new Date().toISOString(),
      });
    }
  },

  logAction: async (action: Omit<AdminAction, 'id' | 'created_at'>) => {
    try {
      await adminService.logAction(action);
      await get().fetchAdminActions();
    } catch (error: any) {
      console.error('[AdminStore] Failed to log admin action:', error);
    }
  },
})); 

// For debugging
if (typeof window !== 'undefined') {
  (window as any).useAdminStore = useAdminStore;
} 